<script setup lang="ts">
import ArrowLeftRound from '@assets/icons/arrow-left-round.svg';
import ChevronRight from '@assets/icons/chevron-right.svg';
import JrnysLogo from '@assets/icons/jrnys-logo-new.svg';
import RxIcon from '@assets/icons/rx.svg';
import CloseX from '@assets/icons/x.svg';
import { getUserMenuItemsForSession } from '@helpers/user-menu';
import { useUserStore } from '@stores/index';
import { computed, ref, watch } from 'vue';
import MenuCard from './MenuCard.vue';
import SiteHeaderUserMenu from './SiteHeaderUserMenu.vue';
import type { NavItem } from './types';

const props = defineProps<{
  modelValue: boolean;
  navItems: NavItem[];
  isLoggedIn?: boolean;
}>();

const userStore = useUserStore();

const isLoggedIn = computed(() => props.isLoggedIn ?? false);

const userMenuItems = computed(() => {
  if (!isLoggedIn.value) {
    return [];
  }
  return getUserMenuItemsForSession(userStore.info);
});

watch(isLoggedIn, async (loggedIn) => {
  if (loggedIn && !userStore.info) {
    await userStore.getInfo();
  }
}, { immediate: true });

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'login'): void;
  (e: 'signup'): void;
}>();

const activePanel = ref<NavItem | null>(null);

const defaultCards = [
  {
    title: 'Discover treatments',
    image: 'https://uat.jrnys.com/jrnys/images/campaign-page/Stocksy_txpc66d6a4eH2j300_OriginalDelivery_5011594_edit-1.jpg?v=1711977280',
    href: '#',
  },
  {
    title: 'See if you are eligible',
    image: 'https://uat.jrnys.com/jrnys/images/campaign-page/Stocksy_txpc66d6a4eH2j300_OriginalDelivery_5011594_edit-1.jpg?v=1711977280',
    href: '#',
  },
];
const panelDirection = ref<'forward' | 'back'>('forward');

const close = () => {
  emit('update:modelValue', false);
  // Reset panel after drawer closes
  setTimeout(() => {
    activePanel.value = null;
  }, 300);
};

const openSubPanel = (item: NavItem) => {
  if (item.children?.length || item.cards?.length) {
    panelDirection.value = 'forward';
    activePanel.value = item;
  }
};

const goBack = () => {
  panelDirection.value = 'back';
  activePanel.value = null;
};

// Reset sub-panel when drawer closes
watch(() => props.modelValue, (val) => {
  if (!val) {
    setTimeout(() => {
      activePanel.value = null;
    }, 300);
  }
});
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-40 bg-black/40"
        @click="close"
      />
    </Transition>

    <!-- Drawer Panel -->
    <Transition name="slide-left">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex flex-col bg-white"
      >
        <!-- Drawer Header -->
        <div class="relative flex items-center justify-center p-20">
          <button
            type="button"
            class="absolute left-20 flex size-36 items-center justify-center text-off-black-500 transition-colors hover:text-turquoise"
            @click="close"
          >
            <CloseX class="size-20" />
          </button>
          <JrnysLogo class="w-72 h-32 text-off-black-500" />
          <div
            v-if="isLoggedIn"
            class="absolute right-20"
          >
            <SiteHeaderUserMenu
              :items="userMenuItems"
              icon-color="dark"
              variant="mobile"
            />
          </div>
        </div>

        <!-- Panel Content -->
        <div class="relative mt-24 flex-1 overflow-hidden font-inter">
          <!-- Main Panel -->
          <Transition :name="panelDirection === 'forward' ? 'panel-out' : 'panel-in'">
            <nav
              v-if="!activePanel"
              class="absolute inset-0 flex flex-col overflow-y-auto px-20"
            >
              <ul class="mx-16 flex-1">
                <li
                  v-for="item in navItems"
                  :key="item.href"
                >
                  <a
                    v-if="!item.children?.length && !item.cards?.length"
                    :href="item.href"
                    class="flex items-center py-18 text-18 font-medium text-off-black-500 transition-colors hover:text-turquoise"
                  >
                    {{ item.label }}
                  </a>
                  <button
                    v-else
                    type="button"
                    class="flex w-full items-center justify-between py-18 text-18 font-medium text-off-black-500 transition-colors hover:text-turquoise"
                    @click="openSubPanel(item)"
                  >
                    <span>{{ item.label }}</span>
                    <ChevronRight class="size-20 text-stone-500" />
                  </button>
                </li>
              </ul>

              <!-- Bottom: Login/Sign up when not logged in; no buttons when logged in (profile icon in header) -->
              <div
                v-if="!isLoggedIn"
                class="flex gap-12 py-20"
              >
                <button
                  type="button"
                  class="flex-1 rounded-full border-2 border-off-black-500 px-24 py-12 text-14 font-medium text-off-black-500 transition-colors hover:bg-off-black-500 hover:text-white"
                  @click="$emit('login')"
                >
                  Login
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-full border-2 border-off-black-500 bg-off-black-500 px-24 py-12 text-14 font-medium text-white transition-colors hover:bg-transparent hover:text-off-black-500"
                  @click="$emit('signup')"
                >
                  Sign up
                </button>
              </div>
            </nav>
          </Transition>

          <!-- Sub Panel -->
          <Transition :name="panelDirection === 'forward' ? 'panel-in' : 'panel-out'">
            <div
              v-if="activePanel"
              class="absolute inset-0 flex flex-col overflow-hidden"
            >
              <!-- Sub-panel header (fixed) -->
              <div class="mt-16 flex shrink-0 items-center gap-20 px-20 pb-20">
                <button
                  type="button"
                  @click="goBack"
                >
                  <ArrowLeftRound class="size-32 text-off-black-500" />
                </button>
                <span class="text-18 font-medium text-off-black-500">
                  {{ activePanel.label }}
                </span>
              </div>

              <div class="min-h-0 flex-1 overflow-y-auto px-24">
                <div
                  v-if="activePanel.children?.length"
                >
                  <p class="mb-8 mt-24 text-14 font-light uppercase tracking-[-2%] text-stone-100">
                    {{ activePanel.childrenLabel ?? activePanel.label }}
                  </p>
                  <ul>
                    <li
                      v-for="child in activePanel.children"
                      :key="child.href"
                    >
                      <a
                        :href="child.href"
                        class="flex items-center gap-10 py-14 text-16 font-medium text-off-black-500 transition-colors hover:text-turquoise"
                      >
                        <span>{{ child.label }}</span>
                        <RxIcon
                          v-if="child.icon === 'rx'"
                          class="size-14 shrink-0 text-copper-300"
                        />
                      </a>
                    </li>
                  </ul>
                  <div class="my-12 border-b border-warm-grey-30" />
                </div>

                <!-- Feature cards: 1 per row below 300px, 2 per row from 300px up -->
                <div class="mt-24 flex flex-col gap-12 pb-32 min-[300px]:flex-row min-[300px]:flex-wrap">
                  <MenuCard
                    v-for="card in (activePanel.cards?.length ? activePanel.cards : defaultCards)"
                    :key="card.href"
                    :title="card.title"
                    :image="card.image"
                    :href="card.href"
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
/* Backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Drawer slide in from left */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}
.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}

/* Panel forward: new panel slides in from right */
.panel-in-enter-active,
.panel-in-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.panel-in-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.panel-in-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

/* Panel backward: panel slides out to right */
.panel-out-enter-active,
.panel-out-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.panel-out-enter-from {
  transform: translateX(-30%);
  opacity: 0;
}
.panel-out-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
